'use client'
import React from 'react';
import { Box, CardMedia, CardMediaProps, SxProps } from '@mui/material';
import ReactPlayer from 'react-player';
import JoyPlayer from '../ReactPlayerVideo';
import moment from 'moment';
import Image, { ImageProps } from 'next/image';
import { getYouTubeThumbnail } from '@/app/components/Writing/UrlFormWriting/components/YouTubeThumbnail';

interface BaseCardMediaProps extends ImageProps {
    activeIndex?: number;
    dataLength?: number;
    shouldShowNumbersImg?: boolean;
    handleVideoReady?: () => void;
    onHandleMediaVideoProcess?: (allowTouchMove: boolean) => void;
    mediaType?: 'image' | 'video' | 'url' | 'thumbnail'; // Add mediaType prop to distinguish between image and video
    sx?: SxProps;
    priority?: boolean;
}

const BaseCardMedia: React.FC<BaseCardMediaProps> = ({
    children,
    src = '',
    activeIndex = 1,
    dataLength = 1,
    shouldShowNumbersImg = false,
    handleVideoReady,
    onHandleMediaVideoProcess,
    mediaType = 'image',
    sx = {},
    priority,
    ...other
}) => {
    const [objectFit, setObjectFit] = React.useState<any>(other?.style?.objectFit || 'cover');

    const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
        const img = e.currentTarget;
        const { naturalWidth: w, naturalHeight: h } = img;

        // Desktop only (min-width: 768px logic usually handled by CSS,
        // but here we can check window width if needed, or just let it stay for all and let CSS handle the rest)
        // For now, let's just set the state if it's tall.
        if (h > w && h > 468 && window.innerWidth >= 768) {
            setObjectFit('contain');
        }

        other.onLoad?.(e);
    };

    const ready = (e: any) => {
        handleVideoReady?.();
    };

    return (
        <Box sx={{ position: 'relative', display: 'flex', width: '100%', ...sx }}>
            {['video'].includes(mediaType) ? (
                <JoyPlayer
                    uniqueId={`${src}${moment().millisecond()}`}
                    src={src as string}
                    controls
                    onHandleMediaVideoProcess={onHandleMediaVideoProcess}
                    onReady={ready}
                    width="100%"
                    height="100%"
                />
            ) : (
                (() => {
                    const ytThumbnail = typeof src === 'string' ? getYouTubeThumbnail(src) : null;
                    const displaySrc = ytThumbnail || src;
                    const isExternal = typeof displaySrc === 'string' && (displaySrc.startsWith('http') || displaySrc.startsWith('//'));

                    // Use standard img for YouTube or if we want to bypass Next.js image optimization for external URLs
                    // that might not be in next.config.js yet, to prevent crashes.
                    if (ytThumbnail || (typeof src === 'string' && src.includes('youtube.com'))) {
                         return (
                            <img
                                src={displaySrc as string}
                                alt="Media Feed"
                                draggable={false}
                                onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                onLoad={handleImageLoad as any}
                                style={{
                                    cursor: 'pointer',
                                    ...other?.style,
                                    width: '100%',
                                    height: 'auto',
                                    objectFit: objectFit
                                }}
                            />
                         );
                    }

                    return (
                        <Image
                            src={displaySrc}
                            loading={priority ? undefined : 'lazy'}
                            priority={priority}
                            width={1024}
                            height={0}
                            draggable={false}
                            onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); }}
                            {...other}
                            onLoad={handleImageLoad}
                            style={{
                                cursor: 'pointer',
                                ...other?.style,
                                width: '100%',
                                height: 'auto',
                                objectFit: objectFit
                            }}
                        />
                    );
                })()
            )}
        </Box>
    );
};

export default BaseCardMedia;
